// The Golden Egg: a canvas-drawn 3D-illusion egg (rotating longitude-wrapped
// scales with per-scale lighting, a traveling sheen band, form shading) used
// for the egg-pack cards and the balance chip in the shop. Pure drawing, no
// state: callers own the rAF loop and pass time in seconds.

const TAU = Math.PI * 2;

// Egg silhouette: bezier outline sampled into a width lookup (half-width at
// height v in [0,1]) plus a path builder for clipping/stroking.
const SILHOUETTE_POINTS: [number, number][] = [];
function cubic(p0: [number, number], p1: [number, number], p2: [number, number], p3: [number, number], u: number): [number, number] {
  const a = 1 - u;
  return [
    a * a * a * p0[0] + 3 * a * a * u * p1[0] + 3 * a * u * u * p2[0] + u * u * u * p3[0],
    a * a * a * p0[1] + 3 * a * a * u * p1[1] + 3 * a * u * u * p2[1] + u * u * u * p3[1],
  ];
}
for (let i = 0; i <= 40; i++) SILHOUETTE_POINTS.push(cubic([0.5, 0.1], [0.76, 0.1], [0.82, 0.44], [0.82, 0.58], i / 40));
for (let i = 1; i <= 40; i++) SILHOUETTE_POINTS.push(cubic([0.82, 0.58], [0.82, 0.82], [0.67, 0.94], [0.5, 0.94], i / 40));

function eggHalfWidth(v: number): number {
  let best = 0;
  for (const [px, py] of SILHOUETTE_POINTS) {
    if (Math.abs(py - v) < 0.02) best = Math.max(best, px - 0.5);
  }
  return best;
}

function eggPath(ctx: CanvasRenderingContext2D, s: number) {
  ctx.beginPath();
  ctx.moveTo(s * 0.5, s * 0.1);
  ctx.bezierCurveTo(s * 0.76, s * 0.1, s * 0.82, s * 0.44, s * 0.82, s * 0.58);
  ctx.bezierCurveTo(s * 0.82, s * 0.82, s * 0.67, s * 0.94, s * 0.5, s * 0.94);
  ctx.bezierCurveTo(s * 0.33, s * 0.94, s * 0.18, s * 0.82, s * 0.18, s * 0.58);
  ctx.bezierCurveTo(s * 0.18, s * 0.44, s * 0.24, s * 0.1, s * 0.5, s * 0.1);
  ctx.closePath();
}

// One egg filling an s*s square. dim in [0,1] darkens it (depth layering).
export function drawEgg(ctx: CanvasRenderingContext2D, s: number, t: number, dim = 0) {
  const pulse = 0.6 + 0.4 * Math.sin(t * 2.0);
  const glow = ctx.createRadialGradient(s / 2, s * 0.52, s * 0.05, s / 2, s * 0.52, s * 0.52);
  glow.addColorStop(0, `rgba(255,190,60,${0.2 * pulse})`);
  glow.addColorStop(1, "rgba(255,190,60,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, s, s);
  ctx.save();
  eggPath(ctx, s);
  ctx.clip();
  const base = ctx.createLinearGradient(s * 0.2, s * 0.15, s * 0.85, s * 0.9);
  base.addColorStop(0, "#8a5f14");
  base.addColorStop(0.5, "#6d4a0e");
  base.addColorStop(1, "#4a3007");
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, s, s);
  // Scales: rows of longitude-wrapped bumps rotating with t, lit from upper-left.
  const rot = t * 0.8;
  const light = [-0.62, 0.62];
  for (let row = 0; row < 10; row++) {
    const v = 0.135 + row * 0.082;
    const y = s * v;
    const halfW = eggHalfWidth(v) * s;
    if (halfW < s * 0.02) continue;
    const rr = s * 0.052 * (0.75 + 0.5 * Math.sin(Math.PI * Math.min(1, (v - 0.06) / 0.9)));
    const n = Math.max(6, Math.round((halfW * TAU) / (rr * 1.7)));
    for (let k = 0; k < n; k++) {
      const lam = (k / n) * TAU + (row % 2) * (Math.PI / n) + rot;
      const sin = Math.sin(lam);
      const cos = Math.cos(lam);
      if (cos < 0.03) continue; // back side of the egg
      const x = s / 2 + sin * halfW;
      const b = Math.max(0, Math.min(1, 0.42 + 0.75 * (light[0] * sin + light[1] * cos)));
      const w2 = rr * Math.max(0.25, cos); // foreshorten toward the rim
      const g = ctx.createRadialGradient(x, y - rr * 0.35, rr * 0.1, x, y, rr * 1.15);
      g.addColorStop(0, `rgba(255,${Math.round(205 + 40 * b)},${Math.round(90 + 90 * b)},1)`);
      g.addColorStop(0.55, `rgba(${Math.round(150 + 105 * b)},${Math.round(96 + 96 * b)},${Math.round(20 + 60 * b)},1)`);
      g.addColorStop(1, `rgba(${Math.round(70 + 60 * b)},${Math.round(44 + 40 * b)},${Math.round(8 + 18 * b)},1)`);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(x, y, w2, rr, 0, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = `rgba(40,26,4,${0.35 + 0.2 * (1 - b)})`;
      ctx.lineWidth = Math.max(0.4, s * 0.006);
      ctx.beginPath();
      ctx.ellipse(x, y, w2, rr, 0, 0, TAU);
      ctx.stroke();
    }
  }
  // Form shading + a traveling sheen band.
  const shade = ctx.createRadialGradient(s * 0.38, s * 0.38, s * 0.05, s * 0.5, s * 0.55, s * 0.5);
  shade.addColorStop(0, "rgba(255,240,190,0.20)");
  shade.addColorStop(0.55, "rgba(0,0,0,0)");
  shade.addColorStop(1, "rgba(20,10,0,0.55)");
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, s, s);
  const band = ((t * 0.45) % 1.6) * s * 1.7 - s * 0.6;
  const sheen = ctx.createLinearGradient(band - s * 0.1, 0, band + s * 0.1, 0);
  sheen.addColorStop(0, "rgba(255,255,255,0)");
  sheen.addColorStop(0.5, "rgba(255,246,214,0.28)");
  sheen.addColorStop(1, "rgba(255,255,255,0)");
  ctx.save();
  ctx.translate(s / 2, s / 2);
  ctx.rotate(0.25);
  ctx.translate(-s / 2, -s / 2);
  ctx.fillStyle = sheen;
  ctx.fillRect(band - s * 0.2, -s * 0.2, s * 0.4, s * 1.4);
  ctx.restore();
  ctx.restore();
  ctx.strokeStyle = "rgba(60,38,6,0.9)";
  ctx.lineWidth = s * 0.018;
  eggPath(ctx, s);
  ctx.stroke();
  if (dim > 0) {
    ctx.save();
    eggPath(ctx, s);
    ctx.fillStyle = `rgba(8,6,16,${dim})`;
    ctx.fill();
    ctx.restore();
  }
}

// One egg at (cx,cy) in canvas fractions, scaled/rotated/dimmed; hop lifts it.
function eggAt(ctx: CanvasRenderingContext2D, s: number, t: number, cx: number, cy: number, sc: number, rot: number, dim: number, hop = 0) {
  ctx.save();
  ctx.translate(cx * s, cy * s - hop * s);
  ctx.rotate(rot);
  ctx.scale(sc, sc);
  ctx.translate(-s * 0.5, -s * 0.52);
  drawEgg(ctx, s, t, dim);
  ctx.restore();
}

// Staggered hop curve: each egg jumps once shortly after hover starts.
function hopOf(hoverT: number, i: number): number {
  if (hoverT < 0) return 0;
  const u = hoverT - i * 0.09;
  if (u < 0 || u > 0.5) return 0;
  return Math.sin((u / 0.5) * Math.PI) * 0.05;
}

// Pack compositions: fanned lean-in clusters for 1-3 eggs, trio+peek for 4.
// hoverT = seconds since hover began, or -1 when not hovered.
export function drawEggCluster(ctx: CanvasRenderingContext2D, s: number, t: number, count: number, hoverT: number) {
  ctx.clearRect(0, 0, s, s);
  const h = (i: number) => hopOf(hoverT, i);
  if (count === 1) {
    eggAt(ctx, s, t, 0.5, 0.52, 0.72, 0, 0, h(0));
    return;
  }
  if (count === 2) {
    eggAt(ctx, s, t + 0.9, 0.4, 0.5, 0.58, -0.18, 0.16, h(1));
    eggAt(ctx, s, t, 0.61, 0.55, 0.64, 0.12, 0, h(0));
    return;
  }
  if (count === 3) {
    eggAt(ctx, s, t + 0.9, 0.3, 0.52, 0.52, -0.26, 0.18, h(1));
    eggAt(ctx, s, t + 1.7, 0.7, 0.52, 0.52, 0.26, 0.18, h(2));
    eggAt(ctx, s, t, 0.5, 0.55, 0.62, 0, 0, h(0));
    return;
  }
  eggAt(ctx, s, t + 2.0, 0.5, 0.34, 0.4, 0.05, 0.38, h(3));
  eggAt(ctx, s, t + 0.9, 0.3, 0.54, 0.52, -0.26, 0.16, h(1));
  eggAt(ctx, s, t + 1.5, 0.7, 0.54, 0.52, 0.26, 0.16, h(2));
  eggAt(ctx, s, t, 0.5, 0.58, 0.6, 0, 0, h(0));
}

// A small static egg as a data URL, for price tags and labels where a live
// rAF canvas per tile would be waste. Drawn once per size and memoized.
const iconCache = new Map<number, string>();
export function eggIconUrl(size = 36): string {
  const hit = iconCache.get(size);
  if (hit) return hit;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  drawEgg(ctx, size, 1.25);
  const url = canvas.toDataURL();
  iconCache.set(size, url);
  return url;
}

// Top-tier extra: rising embers in the well while hovered.
export function drawEmbers(ctx: CanvasRenderingContext2D, s: number, t: number) {
  for (let k = 0; k < 7; k++) {
    const u = (t * 0.6 + k * 0.143) % 1;
    const x = s * (0.12 + ((k * 0.131) % 0.76)) + Math.sin(t * 2 + k) * s * 0.02;
    ctx.globalAlpha = (1 - u) * 0.8;
    ctx.fillStyle = k % 2 ? "#ffcf4d" : "#ffe9a3";
    ctx.beginPath();
    ctx.arc(x, s * 0.95 - u * s * 0.85, s * 0.014, 0, TAU);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}
